import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useCommandPaletteStore } from '@/modules/project/hooks';
import {
  FolderKanban,
  CheckSquare,
  Users,
  Settings,
  Shield,
  FileText,
  Flag,
} from 'lucide-react';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { 
    tasks, 
    projects, 
    milestones, 
    members, 
    loading, 
    search: searchData, 
    reset 
  } = useCommandPaletteStore();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Global keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Search with debounce
  useEffect(() => {
    if (!open || !currentOrganization) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchData(currentOrganization.id, search);
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [open, search, currentOrganization, searchData]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      reset();
      setSearch('');
    }
  }, [open, reset]);

  const handleSelect = useCallback((callback: () => void) => {
    setOpen(false);
    callback();
  }, []);

  const navigationItems = [
    { icon: Users, label: 'Membres', path: '/admin/access?tab=members' },
    // { icon: Shield, label: 'Rôles', path: '/roles' },
    { icon: FileText, label: "Journal d'Audit", path: '/admin/audit-logs' },
    { icon: Settings, label: 'Paramètres', path: '/admin/settings' },
  ];

  const getMilestoneStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'text-green-500';
      case 'in_progress': return 'text-blue-500';
      default: return 'text-slate-400';
    }
  };

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  const hasSearchResults = tasks.length > 0 || projects.length > 0 || milestones.length > 0 || members.length > 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Rechercher tâches, projets, jalons, membres..."
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        <CommandEmpty>
          {loading ? 'Recherche...' : 'Aucun résultat trouvé.'}
        </CommandEmpty>

        {/* Tasks results */}
        {tasks.length > 0 && (
          <CommandGroup heading="Tâches">
            {tasks.map((task) => (
              <CommandItem
                key={task.id}
                value={`task-${task.id}`}
                onSelect={() => handleSelect(() => navigate(`/projects/${task.project_id}?task=${task.id}`))}
              >
                <CheckSquare className="mr-2 h-4 w-4 text-indigo-500" />
                <span className="flex-1 truncate">{task.title}</span>
                <span className="text-xs text-muted-foreground ml-2">{task.project_name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Milestones results */}
        {milestones.length > 0 && (
          <CommandGroup heading="Jalons">
            {milestones.map((milestone) => (
              <CommandItem
                key={milestone.id}
                value={`milestone-${milestone.id}`}
                onSelect={() => handleSelect(() => navigate(`/projects/${milestone.project_id}/milestones?milestone=${milestone.id}`))}
              >
                <Flag className={`mr-2 h-4 w-4 ${getMilestoneStatusColor(milestone.status)}`} />
                <span className="flex-1 truncate">{milestone.title}</span>
                <span className="text-xs text-muted-foreground ml-2">{milestone.project_name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Projects results */}
        {projects.length > 0 && (
          <CommandGroup heading="Projets">
            {projects.map((project) => (
              <CommandItem
                key={project.id}
                value={`project-${project.id}`}
                onSelect={() => handleSelect(() => navigate(`/projects/${project.id}`))}
              >
                <FolderKanban className="mr-2 h-4 w-4 text-violet-500" />
                <span>{project.name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Members results */}
        {members.length > 0 && (
          <CommandGroup heading="Membres">
            {members.map((member) => (
              <CommandItem
                key={member.id}
                value={`member-${member.id}-${member.email}`}
                onSelect={() => handleSelect(() => navigate(`/admin/access?tab=members`))}
              >
                <Avatar className="mr-2 h-5 w-5">
                  <AvatarImage src={member.avatar_url || undefined} />
                  <AvatarFallback className="text-[10px] bg-slate-200 dark:bg-slate-700">
                    {getInitials(member.full_name, member.email)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate">{member.full_name || member.email}</span>
                {member.full_name && (
                  <span className="text-xs text-muted-foreground ml-2">{member.email}</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Quick navigation - only show when no search or no results */}
        {(!search || !hasSearchResults) && (
          <CommandGroup heading="Navigation">
            {navigationItems.map((item) => (
              <CommandItem
                key={item.path}
                value={`nav-${item.label}`}
                onSelect={() => handleSelect(() => navigate(item.path))}
              >
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
